import { z } from 'zod';
import { personSchema } from '../people/person.js';
/**
 * Ce qu'un agent **en ligne** écrit directement sur une demande de sa tournée : les coordonnées de
 * la personne qu'il a devant lui (décision du développeur, 23/09/2026).
 *
 * ⚠️ **Une écriture directe, hors proposition** — la seconde exception à « le terrain propose »
 * (D4), après le report de rendez-vous, et sous la même condition : **en ligne seulement**, jamais
 * dans l'outbox. L'agent a la personne en face de lui ; faire attendre la validation du bureau pour
 * un numéro de téléphone laisserait la demande avec un contact faux pendant des jours.
 *
 * ⚠️ **Deux rôles, pas trois** (M15). Le contact du rendez-vous (`Inquiry.appointment_contact_id`)
 * et le propriétaire (`Site.owner_id`) sont des faits de la demande et du lieu. La **personne
 * rencontrée** n'en est pas un : elle appartient au contrôle, voyage dans la proposition
 * (`metPerson`) et n'entre en base qu'à la validation (M20). Le demandeur payeur, lui, n'est pas
 * sur place — c'est d'ordinaire l'agence ou le notaire.
 *
 * ⚠️ **Au moins un des deux blocs** : un corps vide ne demande rien.
 */
/**
 * Un champ **corrigé** : absent, il reste tel qu'il est ; `null` ou vide, il s'efface ; sinon il
 * prend la valeur saisie, repliée.
 *
 * ⚠️ **L'absence ne vaut pas `null`, contrairement à la création** (`freeLabelSchema`). Une
 * correction ne porte que ce que l'agent a touché : la tablette ne connaît pas tout de la personne,
 * et lire un champ absent comme un effacement ferait perdre ce qu'elle n'affiche pas.
 */
const foldedLabelSchema = z
    .string()
    .nullable()
    .transform((text) => {
    const trimmed = text?.trim() ?? '';
    return trimmed === '' ? null : trimmed;
});
// ⚠️ `.optional()` **à l'extérieur** : posé avant la transformation, il rendrait la clé obligatoire
// dans le type de sortie, et chaque appelant devrait envoyer toute la fiche — l'inverse du but.
const correctedLabelSchema = foldedLabelSchema.optional();
/**
 * Une personne telle que l'agent la **corrige**, avec la sémantique d'une correction : un champ
 * absent reste tel quel.
 *
 * ⚠️ **Un nom connu ne s'efface pas** (`min(1)`) : la correction le remplace, elle ne le retire
 * pas — retirer un nom perdrait une donnée de terrain que rien ne reconstitue (M21). Absent, il
 * reprend celui de la personne en place ; s'il n'y en a aucune, la personne neuve n'en a pas : un
 * téléphone seul se garde et s'affiche comme un autre (M24, #268).
 *
 * ⚠️ **L'e-mail s'enregistre même hors format, et s'avertit** (M21) : l'agent est seul devant
 * l'écran, et refuser l'obligerait à inventer une adresse ou à perdre les autres corrections.
 * La tablette affiche `emailWarning` sous le champ ; aucun envoi ne part vers une adresse hors
 * format (`usableEmail`), et le bureau rappelle. La création au bureau suit la même règle
 * (`createInquirySchema`). ⚠️ La confirmation de rendez-vous part à la **personne présente**
 * (`confirmationRecipientOf`, #407) ; l'avis de passage, à la première personne qui a une
 * adresse — présente, puis demandeur, puis propriétaire (`passage-notices.repository.ts`).
 */
const correctedPersonSchema = z.strictObject({
    lastName: z.string().trim().min(1).optional(),
    firstName: correctedLabelSchema,
    phone: correctedLabelSchema,
    email: correctedLabelSchema,
});
/**
 * Ce que « En qualité de » propose, dans l'ordre de GHP (recette du 20/10/2026, #504). Une
 * **suggestion**, pas une liste fermée : le champ reste libre, et une autre qualité s'écrit.
 * Le terrain et le bureau lisent celle-ci.
 */
export const CAPACITY_SUGGESTIONS = [
    'Propriétaire',
    'Locataire',
    'Notaire',
    'Agence',
    'Voisin',
];
/**
 * Les rôles qu'une personne présente peut **aussi** tenir sur la demande (#407) : la case
 * « Même personne que le demandeur » ou « … que le propriétaire ».
 *
 * ⚠️ **Le rapprochement est fait par un humain, jamais déduit** : `Person` n'a pas de critère
 * d'identité (M24), et deux homonymes liés à tort recevraient les corrections l'un de l'autre.
 * Coché, une seule personne en base tient les deux rôles, et une correction de l'un se voit sur
 * l'autre (M15). ⚠️ Depuis #555, le lien au **demandeur** est le défaut d'un bloc « Contact »
 * unique : l'humain dit « une autre personne sera présente », l'écran ne le déduit jamais (M24).
 */
export const APPOINTMENT_CONTACT_LINKS = ['applicant', 'owner'];
export const appointmentContactLinkSchema = z.enum(APPOINTMENT_CONTACT_LINKS);
/** Les cases cochées, dédoublonnées ; absentes, aucune — jamais cochées d'office. */
export const appointmentContactLinksSchema = z
    .array(appointmentContactLinkSchema)
    .nullish()
    .transform((links) => [...new Set(links ?? [])]);
export const updateInquiryContactsSchema = z
    .strictObject({
    /**
     * Le **contact du rendez-vous**, et sa qualité — « En qualité de », propriétaire, locataire…
     * (`Inquiry.appointment_contact_capacity`, M32). C'est lui que la tournée affiche.
     */
    appointmentContact: correctedPersonSchema.extend({ capacity: correctedLabelSchema }).optional(),
    /** Le **demandeur** corrigé (#434), depuis « Modifier la demande ». */
    applicant: correctedPersonSchema.optional(),
    /**
     * Le **propriétaire** du lieu (`Site.owner_id`, M15) — il ne se qualifie pas, il l'est.
     *
     * Lui seul porte une adresse et une commune : le bloc « Propriétaire » du rapport papier les
     * imprime, et ce n'est pas toujours l'adresse du lieu contrôlé (#350).
     */
    owner: correctedPersonSchema
        .extend({ address: correctedLabelSchema, municipality: correctedLabelSchema })
        .optional(),
    /**
     * Les rôles que la personne présente tient **après** la correction, tels que le bureau les a
     * laissés cochés (#407) : décochée, une case défait le lien et le rôle garde sa personne ;
     * cochée, il prend la personne présente.
     *
     * ⚠️ **Absente, les liens en place suivent — sauf si le nom change** : la tablette ne connaît
     * pas les liens, et un autre nom désigne une autre personne. Le propriétaire lié garde alors
     * la sienne au lieu d'être renommé. Défaire un lien ne perd rien ; en poser un reste un geste
     * humain, jamais une déduction.
     */
    appointmentContactSameAs: z
        .array(appointmentContactLinkSchema)
        .transform((links) => [...new Set(links)])
        .optional(),
})
    .refine((body) => body.appointmentContact !== undefined ||
    body.applicant !== undefined ||
    body.owner !== undefined ||
    body.appointmentContactSameAs !== undefined, {
    error: 'aucune coordonnée à enregistrer — `appointmentContact`, `applicant`, `owner` ou ' +
        '`appointmentContactSameAs` est attendu',
});
/**
 * Une personne telle que le bureau la lit sur une demande. ⚠️ L'e-mail en `string`, pas
 * `z.email()` : la base porte des valeurs reprises hors format (« tel uniquement »), et l'écran
 * doit les montrer pour qu'on les corrige.
 */
const officePersonSchema = personSchema
    .pick({ lastName: true, firstName: true, phone: true })
    .extend({ email: z.string().nullable() });
/**
 * Les coordonnées d'une demande telles que « Gérer une demande » les montre et les corrige (#407) :
 * les trois rôles de M15 qui vivent sur la demande et son lieu, la qualité de la personne
 * présente, et les rôles qu'elle tient aussi.
 *
 * ⚠️ `appointmentContactSameAs` se lit sur les **identifiants** (la même ligne `person`), jamais
 * sur les noms : deux lignes qui se ressemblent ne sont pas liées.
 */
export const inquiryContactsSchema = z.object({
    applicant: officePersonSchema.nullable(),
    appointmentContact: officePersonSchema.nullable(),
    appointmentContactCapacity: z.string().nullable(),
    owner: officePersonSchema
        .extend({ address: z.string().nullable(), municipality: z.string().nullable() })
        .nullable(),
    appointmentContactSameAs: z.array(appointmentContactLinkSchema),
});
/**
 * À qui la confirmation de rendez-vous peut partir : la personne présente, ou le demandeur quand le
 * bureau le choisit (#407).
 */
export const CONFIRMATION_RECIPIENTS = ['appointmentContact', 'applicant'];
export const confirmationRecipientRoleSchema = z.enum(CONFIRMATION_RECIPIENTS);
/** Pourquoi la confirmation ne part pas : personne, pas d'adresse, ou une adresse hors format. */
export const CONFIRMATION_GAPS = ['nobody', 'no_email', 'invalid_email'];
/** La personne à qui la confirmation part, telle que l'écran la nomme — rien d'autre d'elle. */
const recipientPersonSchema = z.object({
    lastName: z.string().nullable(),
    firstName: z.string().nullable(),
    email: z.string().nullable(),
});
/**
 * Le destinataire **sur le fil** (`ScheduledAppointment.recipient`) : à qui la confirmation est
 * partie, ou pourquoi elle n'est pas partie — la sortie de `confirmationRecipientOf`.
 */
export const confirmationRecipientSchema = z.union([
    z.object({
        role: confirmationRecipientRoleSchema,
        person: recipientPersonSchema,
        email: z.string(),
    }),
    z.object({
        role: confirmationRecipientRoleSchema,
        person: recipientPersonSchema.nullable(),
        email: z.null(),
        reason: z.enum(CONFIRMATION_GAPS),
    }),
]);
/**
 * Le destinataire de la confirmation, ou la raison pour laquelle elle ne part pas (#407).
 *
 * Elle part à la **personne présente** : c'est elle qui ouvrira la porte, et sur une vente le
 * demandeur est souvent l'agence ou le notaire. Sans personne présente, c'est le demandeur — celui
 * que la tournée affiche alors (`contactOf`).
 *
 * ⚠️ **Une personne présente sans e-mail ne fait pas basculer l'envoi au demandeur** : prévenir
 * l'agence à la place de l'occupant est un choix que le bureau fait, à l'écran (`chosen`), pas un
 * repli silencieux. ⚠️ **Une adresse hors format ne reçoit rien** (M21) : la règle de format est
 * celle de `personSchema`, la même que l'API applique à l'envoi (`usableEmail`).
 *
 * Une seule écriture de la règle, lue par l'API qui envoie et par l'écran qui annonce : deux
 * copies finiraient par annoncer un destinataire qui ne reçoit rien.
 */
export function confirmationRecipientOf(contacts, chosen = 'appointmentContact') {
    const role = chosen === 'appointmentContact' && contacts.appointmentContact === null ? 'applicant' : chosen;
    const person = contacts[role];
    if (person === null)
        return { role, person, email: null, reason: 'nobody' };
    const email = person.email?.trim() ?? '';
    if (email === '')
        return { role, person, email: null, reason: 'no_email' };
    if (!personSchema.shape.email.safeParse(email).success) {
        return { role, person, email: null, reason: 'invalid_email' };
    }
    return { role, person, email };
}
//# sourceMappingURL=contacts.js.map